<template>
  <v-infinite-scroll color="black" mode="manual" style="height: 100%; max-height: 400px" @load="loadMore">
    <v-text-field class="bg-white" :class="row === 1 ? '' : 'w-25'" v-model="servicesSearch" placeholder="Type to begin filtering..." clearable @click:clear="clearSearch" density="compact" variant="outlined" hide-details />
    <v-checkbox v-model="checkAllServices" @change="checkAll" label="Select All" density="compact" />
    <v-row>
      <template v-for="(service, index) in filteredServices" :key="service.serviceId">
        <v-col :md="row === 1 ? 12 : 3" v-if="Number(index) < limitServices" class="py-0" cols="12">
          <v-checkbox density="compact" :label="serviceName(service.serviceName)" v-model="selectedServicesData" :value="service.serviceId"
                      hide-details append-icon="mdi-pencil-box-outline" @click:append="goEditServicePage(service.serviceId)" />
        </v-col>
      </template>
    </v-row>
    <template v-slot:empty>
      No more results
    </template>
  </v-infinite-scroll>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, PropType } from 'vue';
import { useRouter } from 'vue-router';
import crApi from "../service/CrApi";
import securityService from "../service/SecurityService";

const router = useRouter();
const props = defineProps({
  services: Array as PropType<string[]>,
  modelValue: Array as PropType<string[]>,
  servicesSearchText: String,
  selectAll: Boolean,
  row: { type: Number as PropType<number>, default: 3 },
  showClearBtn: Boolean,
  limit: Boolean,
  selectedServicesCheckbox: Boolean,
  deletedServices: Boolean
});

const emit = defineEmits(['update:modelValue', 'update:selectAll']);
const servicesSearch = ref(props.servicesSearchText || '');
const debouncedServicesSearch = ref(servicesSearch.value);
const checkAllServices = ref(props.selectAll || props.modelValue.length === (props.services ? props.services.length : 0));
const selectedServicesData = ref(props.modelValue.slice());
const isLoading = ref(false);
const offset = ref(0);
const limit = ref(25);
const services = ref([]);
const limitNum = computed(() => (props.limit ? props.row * 3 : Number.MAX_SAFE_INTEGER));
const limitServices = ref<number>(limitNum.value);

const filteredServices = computed(() => {
  return services.value.filter(service => {
    const matchSearch = service.serviceName.toLowerCase().includes(debouncedServicesSearch.value.toLowerCase());
    return matchSearch || selectedServicesData.value.includes(service.serviceId);
  });
});

onMounted(async () => {
  await loadMore({}, true, false);
  updateCheckAllState();
});

watch(servicesSearch, (newSearch) => {
  debounceSearch(newSearch);
});

watch(selectedServicesData, () => {
  updateCheckAllState();
  emit('update:modelValue', selectedServicesData.value);
});

let debounceTimeout: ReturnType<typeof setTimeout>;
function debounceSearch(newSearch: string) {
  clearTimeout(debounceTimeout);
  debounceTimeout = setTimeout(() => {
    debouncedServicesSearch.value = newSearch;
    offset.value = 0;
    services.value = [];
    loadMore({}, false, true);
  }, 1000);
}

function goEditServicePage(serviceId) {
  router.push({ path: `/account/services/${serviceId}/type/` });
}

function checkAll() {
  if (checkAllServices.value) {
    selectedServicesData.value = services.value.map(service => service.serviceId);
  } else {
    selectedServicesData.value = [];
  }
  emit('update:modelValue', selectedServicesData.value);
  emit('update:selectAll', checkAllServices.value);
}

function updateCheckAllState() {
  if (services.value.length > 0 && services.value.every(service => selectedServicesData.value.includes(service.serviceId))) {
    checkAllServices.value = true;
  } else {
    checkAllServices.value = false;
  }
}

async function loadMore({ done }: { done?: (arg: string) => void } = {}, firstLoad = false, search = false) {
  if (isLoading.value) {
    return;
  }
  isLoading.value = true;

  try {
    const response = await crApi.requestV2('service/retrieve/', {
      sortBy: 'servicename',
      response: 'extra',
      sortOrder: 'asc',
      website_token: securityService.getToken(),
      excludeTypes: ['webview', 'scanwedge'],
      offset: offset.value,
      limit: limit.value,
      serviceName: debouncedServicesSearch.value
    });
    if (response && response.status === 1) {
      if (search) {
        services.value.length = 0;
      }

      const newServices = response.service.filter(service => !services.value.some(s => s.serviceId === service.serviceId));
      services.value.push(...newServices);
      offset.value += response.service.length;
      limitServices.value = services.value.length;

      if (checkAllServices.value && !search) {
        newServices.forEach(service => {
          if (!selectedServicesData.value.includes(service.serviceId)) {
            selectedServicesData.value.push(service.serviceId);
          }
        });
      }

      if (firstLoad && props.modelValue.length > 0) {
        selectedServicesData.value = [...props.modelValue];
      }

      if (done) {
        done(response.service.length === 0 ? 'empty' : 'done');
      }
    }
  } catch (error) {
    console.error('Error loading services:', error);
  } finally {
    isLoading.value = false;
    updateCheckAllState();
  }
}

function serviceName(name: string) {
  return name.length > 29 ? `${name.substring(0, 29)}...` : name;
}

function clearSearch() {
  servicesSearch.value = '';
  checkAllServices.value = selectedServicesData.value.length === services.value.length;
}
</script>
